import { useState } from 'react';
import { ResponsiveContainer, LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { formatCurrency } from '../lib/utils';

const C_A = '#94a3b8';
const C_B = '#c2410c';
const tl = (v) => formatCurrency(v);
const short = (v) => (Math.abs(v) >= 1000 ? `${Math.round(v / 1000)}k` : v);

const dayList = (start, end) => {
  const out = [];
  for (let d = new Date(start + 'T00:00:00'); d <= new Date(end + 'T00:00:00'); d.setDate(d.getDate() + 1)) {
    out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
  }
  return out;
};

// Günlük seri: iki dönem gün sırasına göre üst üste; kayıt olmayan gün boş (sıfır değil) bırakılır
const alignDaily = (A, B, dailyA, dailyB, missingAsNull) => {
  const da = dayList(A.start, A.end), db = dayList(B.start, B.end);
  const n = Math.max(da.length, db.length);
  return Array.from({ length: n }, (_, i) => ({
    gun: i + 1,
    a: da[i] !== undefined ? (dailyA[da[i]] ?? (missingAsNull ? null : 0)) : null,
    b: db[i] !== undefined ? (dailyB[db[i]] ?? (missingAsNull ? null : 0)) : null,
    aDate: da[i], bDate: db[i],
  }));
};

const monthly = (daily) => {
  const m = {};
  Object.entries(daily).forEach(([d, v]) => { m[d.slice(0, 7)] = (m[d.slice(0, 7)] || 0) + v; });
  return m;
};

const ChartCard = ({ title, children, testid, note }) => (
  <Card data-testid={testid}>
    <CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
    <CardContent>
      <div className="h-64 w-full">{children}</div>
      {note && <p className="text-xs text-muted-foreground mt-2">{note}</p>}
    </CardContent>
  </Card>
);

const DualTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-card border border-border rounded p-2 text-xs space-y-0.5">
      <div>Önceki {p.aDate || '—'}: {p.a == null ? 'kayıt yok' : tl(p.a)}</div>
      <div>Yeni {p.bDate || '—'}: {p.b == null ? 'kayıt yok' : tl(p.b)}</div>
    </div>
  );
};

export const RevenueDailyChart = ({ data }) => {
  const rows = alignDaily(data.period_a, data.period_b, data.period_a.revenue.daily, data.period_b.revenue.daily, true);
  return (
    <ChartCard title="Günlük ciro (gün sırasına göre)" testid="sa-chart-revenue" note="Ciro girilmeyen günler boş bırakıldı, sıfır kabul edilmedi.">
      <ResponsiveContainer>
        <LineChart data={rows}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="gun" fontSize={11} /><YAxis tickFormatter={short} fontSize={11} width={45} /><Tooltip content={<DualTooltip />} /><Legend />
          <Line type="monotone" dataKey="a" name="Önceki" stroke={C_A} dot={false} connectNulls={false} />
          <Line type="monotone" dataKey="b" name="Yeni" stroke={C_B} dot={false} connectNulls={false} />
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

export const CategoryBarChart = ({ title, entries, testid }) => {
  const rows = entries.map(([, r]) => ({ name: r.label, a: r.a_total, b: r.b_total }));
  return (
    <ChartCard title={title} testid={testid}>
      <ResponsiveContainer>
        <BarChart data={rows}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="name" fontSize={10} interval={0} angle={-25} textAnchor="end" height={60} /><YAxis tickFormatter={short} fontSize={11} width={45} /><Tooltip formatter={(v) => tl(v)} /><Legend />
          <Bar dataKey="a" name="Önceki" fill={C_A} /><Bar dataKey="b" name="Yeni" fill={C_B} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

export const CategoryDailyChart = ({ data, options }) => {
  const [key, setKey] = useState(options[0][0]);
  const src = (P) => (key.startsWith('exp:') ? P.expenses[key.slice(4)] : P.purchases[key]) || { daily: {} };
  const rows = alignDaily(data.period_a, data.period_b, src(data.period_a).daily, src(data.period_b).daily, false);
  return (
    <Card data-testid="sa-chart-category-daily">
      <CardHeader className="pb-2 flex flex-row flex-wrap items-center justify-between gap-2">
        <CardTitle className="text-base">Kategori günlük grafik</CardTitle>
        <select className="border border-border rounded-md bg-background text-sm px-2 py-1 max-w-full" value={key} onChange={(e) => setKey(e.target.value)} data-testid="sa-chart-category-select">
          {options.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </CardHeader>
      <CardContent>
        <div className="h-64 w-full">
          <ResponsiveContainer>
            <BarChart data={rows}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="gun" fontSize={11} /><YAxis tickFormatter={short} fontSize={11} width={45} /><Tooltip content={<DualTooltip />} /><Legend />
              <Bar dataKey="a" name="Önceki" fill={C_A} /><Bar dataKey="b" name="Yeni" fill={C_B} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-muted-foreground mt-2">Alım kaydı olmayan günde tutar 0'dır (alım yapılmamış).</p>
      </CardContent>
    </Card>
  );
};

export const MonthlyChart = ({ data }) => {
  const rev = { ...monthly(data.period_a.revenue.daily), ...monthly(data.period_b.revenue.daily) };
  const pur = {};
  [data.period_a, data.period_b].forEach((P) => Object.values(P.purchases).forEach((c) => Object.entries(monthly(c.daily)).forEach(([m, v]) => { pur[m] = (pur[m] || 0) + v; })));
  const exp = {};
  [data.period_a, data.period_b].forEach((P) => Object.values(P.expenses).forEach((c) => Object.entries(monthly(c.daily)).forEach(([m, v]) => { exp[m] = (exp[m] || 0) + v; })));
  const months = [...new Set([...Object.keys(rev), ...Object.keys(pur), ...Object.keys(exp)])].sort();
  if (months.length < 2) return null;
  const rows = months.map((m) => ({ ay: m, ciro: rev[m] ?? null, alim: pur[m] || 0, gider: exp[m] || 0 }));
  return (
    <ChartCard title="Aylık ciro / satın alma / gider" testid="sa-chart-monthly" note="Ciro eksik günler içeren aylarda ciro toplamı eksiktir; kapsama bilgisine bakınız.">
      <ResponsiveContainer>
        <BarChart data={rows}><CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="ay" fontSize={11} /><YAxis tickFormatter={short} fontSize={11} width={50} /><Tooltip formatter={(v) => (v == null ? 'kayıt yok' : tl(v))} /><Legend />
          <Bar dataKey="ciro" name="Ciro" fill="#0f766e" /><Bar dataKey="alim" name="Satın alma" fill={C_B} /><Bar dataKey="gider" name="Gider" fill={C_A} />
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
};

export const CoverageCard = ({ data }) => {
  const P = [['Önceki', data.period_a], ['Yeni', data.period_b]];
  return (
    <Card data-testid="sa-coverage">
      <CardHeader className="pb-2"><CardTitle className="text-base">Veri kapsama</CardTitle></CardHeader>
      <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        {P.map(([l, p]) => (
          <div key={l} className="space-y-1">
            <p className="font-medium">{l}: {p.start} – {p.end} ({p.days} gün)</p>
            <p>Ciro kaydı: {p.revenue.days_with_data}/{p.days} gün (%{p.revenue.coverage_pct}){p.revenue.monthly_split_days ? ` · ${p.revenue.monthly_split_days} gün aylık bölünmüş` : ''}</p>
            {p.revenue.missing_days.length > 0 && <p className="text-xs text-muted-foreground break-words">Eksik günler: {p.revenue.missing_days.slice(0, 40).join(', ')}{p.revenue.missing_days.length > 40 ? ' …' : ''}</p>}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export const PosPendingCard = () => (
  <Card data-testid="sa-pos-pending">
    <CardHeader className="pb-2"><CardTitle className="text-base">POS verileri</CardTitle></CardHeader>
    <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
      {['Ödeme dağılımı (POS)', 'Menü kalemi satışları', 'Saatlik satış yoğunluğu'].map((t) => (
        <div key={t} className="border border-dashed border-border rounded-lg p-3">
          <p className="font-medium">{t}</p>
          <p className="text-muted-foreground text-xs mt-1">Veri yok – Orion POS entegrasyonu bekleniyor</p>
        </div>
      ))}
    </CardContent>
  </Card>
);

export const ExcludedTable = ({ items }) => (
  <Card data-testid="sa-excluded-table">
    <CardHeader className="pb-2"><CardTitle className="text-base">Kontrol Gerekli – Toplama katılmayan giderler ({items.length})</CardTitle></CardHeader>
    <CardContent className="overflow-x-auto max-h-80 overflow-y-auto">
      {items.length === 0 ? <p className="text-sm text-muted-foreground">Bu dönemlerde hariç tutulan gider yok.</p> : (
        <Table className="text-sm">
          <TableHeader><TableRow><TableHead>Tarih</TableHead><TableHead>Kategori</TableHead><TableHead>Açıklama</TableHead><TableHead className="text-right">Tutar</TableHead><TableHead>Neden</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((x) => (
              <TableRow key={x.id}><TableCell>{x.date}</TableCell><TableCell>{x.category}</TableCell><TableCell className="max-w-[220px] break-words">{x.description}</TableCell><TableCell className="text-right">{tl(x.amount)}</TableCell><TableCell className="text-xs">{x.reason}</TableCell></TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <p className="text-xs text-muted-foreground mt-2">Kayıtlar silinmedi veya değiştirilmedi; yalnızca analiz toplamına katılmadı.</p>
    </CardContent>
  </Card>
);
